import { useState } from 'react'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import { loadTaskLists, saveTaskLists, type TaskList } from '../lib/dailyLiving'
import { deleteYouthNote, loadYouthNotes, saveYouthNote } from '../lib/youthNotes'

export function Lists() {
  const { currentUser, interfaceMode, accessProfile } = useApp()
  const isYouth = currentUser.role === 'participant' && accessProfile.accountType === 'youth'

  if (isYouth) return <YouthNotes participantId={currentUser.id} />

  const canCreate = ['participant', 'rep', 'support_worker'].includes(currentUser.role)
  const [lists, setLists] = useState<TaskList[]>(loadTaskLists)
  const [title, setTitle] = useState('')
  const [itemsText, setItemsText] = useState('')

  const persist = (next: TaskList[]) => { setLists(next); saveTaskLists(next) }

  const create = () => {
    const lines = itemsText.split('\n').map((line) => line.trim()).filter(Boolean)
    if (!canCreate || !title.trim() || lines.length === 0) return
    persist([...lists, { id: `list-${Date.now()}`, title: title.trim(), createdByUserId: currentUser.id, createdAt: new Date().toISOString(), items: lines.map((text, index) => ({ id: `list-item-${Date.now()}-${index}`, text, done: false })) }])
    setTitle(''); setItemsText('')
  }

  const toggle = (listId: string, itemId: string) => {
    persist(lists.map((list) => list.id !== listId ? list : { ...list, items: list.items.map((entry) => entry.id !== itemId ? entry : { ...entry, done: !entry.done, completedByUserId: !entry.done ? currentUser.id : undefined, completedAt: !entry.done ? new Date().toISOString() : undefined }) }))
  }

  const reset = (listId: string) => persist(lists.map((list) => list.id !== listId ? list : { ...list, items: list.items.map((item) => ({ ...item, done: false, completedByUserId: undefined, completedAt: undefined })) }))

  return (
    <div>
      <TopBar title="Lists" subtitle="Shared checklists for everyday things" />
      <div className="flex flex-col gap-4 px-4 py-4">
        {canCreate && <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card"><h2 className="text-sm font-extrabold text-slate-900">Create a list</h2><input className="input mt-3" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="List name" /><textarea className="input mt-2" value={itemsText} onChange={(e) => setItemsText(e.target.value)} rows={4} placeholder="Add one item per line" /><button onClick={create} className="mt-3 w-full rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Save list</button></section>}
        {lists.map((list) => {
          const done = list.items.filter((item) => item.done).length
          return <section key={list.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card"><div className="flex items-center justify-between"><h2 className={`${interfaceMode === 'visual' ? 'text-xl' : 'text-base'} font-extrabold text-slate-900`}>{list.title}</h2><span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-bold text-brand-700">{done}/{list.items.length}</span></div><div className="mt-3 flex flex-col gap-2">{list.items.map((item) => <button key={item.id} onClick={() => toggle(list.id, item.id)} className={`flex items-center gap-3 rounded-xl text-left ${interfaceMode === 'visual' ? 'p-4' : 'p-3'} ${item.done ? 'bg-emerald-50' : 'bg-slate-50'}`}><span className={`${interfaceMode === 'visual' ? 'h-10 w-10 text-xl' : 'h-6 w-6 text-xs'} flex shrink-0 items-center justify-center rounded-lg border-2 ${item.done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}>{item.done ? '✓' : ''}</span><span className={`${interfaceMode === 'visual' ? 'text-lg' : 'text-sm'} font-semibold ${item.done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item.text}</span></button>)}</div>{done > 0 && <button onClick={() => reset(list.id)} className="mt-3 text-xs font-bold text-brand-600">Reset list</button>}</section>
        })}
        {lists.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400">No lists yet.</div>}
      </div>
    </div>
  )
}

function YouthNotes({ participantId }: { participantId: string }) {
  const [notes, setNotes] = useState(() => loadYouthNotes(participantId))
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  const add = () => {
    if (!title.trim() && !body.trim()) return
    saveYouthNote(participantId, title.trim() || 'My note', body)
    setNotes(loadYouthNotes(participantId))
    setTitle('')
    setBody('')
    setShowAdd(false)
  }

  const remove = (id: string) => {
    deleteYouthNote(id)
    setNotes(loadYouthNotes(participantId))
  }

  return (
    <div className="min-h-screen bg-sky-50 pb-24">
      <TopBar title="My Notes 📝" subtitle="Write down things I want to remember" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <button onClick={() => setShowAdd((value) => !value)} className="rounded-2xl bg-sky-600 py-3 text-sm font-black text-white shadow-card">{showAdd ? 'Cancel' : '+ Add a note'}</button>

        {showAdd && (
          <section className="rounded-3xl bg-white p-4 shadow-card">
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" />
            <textarea className="input mt-3" value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Write my note here..." />
            <button onClick={add} className="mt-3 w-full rounded-xl bg-sky-600 py-3 text-sm font-black text-white">Save note</button>
          </section>
        )}

        {notes.map((note) => (
          <article key={note.id} className="rounded-3xl bg-white p-5 shadow-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-black text-slate-900">{note.title}</h2>
                {note.body && <p className="mt-2 whitespace-pre-wrap text-base font-semibold leading-6 text-slate-600">{note.body}</p>}
                <p className="mt-3 text-xs font-bold text-slate-400">{new Date(note.updatedAt).toLocaleDateString()}</p>
              </div>
              <button onClick={() => remove(note.id)} className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-500">Delete</button>
            </div>
          </article>
        ))}

        {notes.length === 0 && <div className="rounded-3xl border-2 border-dashed border-sky-200 bg-white p-6 text-center"><div className="text-4xl">📝</div><p className="mt-2 text-base font-black text-slate-700">No notes yet</p><p className="mt-1 text-sm font-semibold text-slate-400">Add something I want to remember.</p></div>}
      </div>
    </div>
  )
}

